import { apiClient, handleApiSuccess } from './api';

export interface CampusPost {
  id: string;
  authorId: string;
  authorName: string;
  authorRole: string;
  authorProfilePicture?: string;
  content: string;
  images?: string[];
  image?: string; // backward compatibility
  likes: number;
  comments: Array<{
    id: string;
    authorId: string;
    authorName: string;
    content: string;
    images?: string[];
    timestamp: string;
  }>;
  timestamp: string;
  isLiked: boolean;
  isPinned?: boolean;
}

export interface CreateCampusPostRequest {
  content: string;
  images?: FileList;
}

export interface GetCampusPostsResponse {
  data: CampusPost[];
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
}

export interface CampusPostApiResponse {
  success: boolean;
  data: CampusPost[];
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
  message?: string;
}

/**
 * Campus Posts Service
 * Handles all campus posts related API calls
 */
export class CampusPostService {
  /**
   * Get campus posts with pagination
   */
  static async getCampusPosts(page: number = 1, limit: number = 10): Promise<GetCampusPostsResponse> {
    console.log('📡 Fetching campus posts - page:', page, 'limit:', limit);
    console.log('🔑 Auth status:', apiClient.isAuthenticated());

    const response = await apiClient.get<CampusPostApiResponse>(`/campus-posts?page=${page}&limit=${limit}`);

    // The apiClient.get returns an ApiResponse, but the actual response from the backend
    // has the structure we defined in CampusPostApiResponse
    const actualResponse = response as unknown as CampusPostApiResponse;

    if (!actualResponse.success) {
      throw new Error(actualResponse.message || 'API request failed');
    }

    const postsData = Array.isArray(actualResponse.data) ? actualResponse.data : [];
    const paginationData = actualResponse.pagination || { page, limit, total: 0 };

    console.log('✅ Got', postsData.length, 'posts, total:', paginationData.total);

    return {
      data: postsData,
      pagination: paginationData
    };
  }

  /**
   * Create a new campus post
   */
  static async createCampusPost(data: CreateCampusPostRequest): Promise<CampusPost> {
    const formData = new FormData();
    formData.append('content', data.content);

    if (data.images && data.images.length > 0) {
      Array.from(data.images).forEach((file) => {
        formData.append(`images`, file);
      });
    }

    const response = await apiClient.post('/campus-posts', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return handleApiSuccess(response);
  }

  /**
   * Like/unlike a campus post
   */
  static async toggleLike(postId: string): Promise<{ isLiked: boolean; likesCount: number }> {
    const response = await apiClient.post(`/campus-posts/${postId}/like`);
    return handleApiSuccess(response);
  }

  /**
   * Add comment to a campus post
   */
  static async addComment(postId: string, content: string, images?: FileList): Promise<void> {
    const formData = new FormData();
    formData.append('content', content);

    if (images && images.length > 0) {
      Array.from(images).forEach((file) => {
        formData.append('images', file);
      });
    }

    const response = await apiClient.post(`/campus-posts/${postId}/comment`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    handleApiSuccess(response); // Just handle success, no return data needed
  }

  /**
   * Delete a campus post
   */
  static async deletePost(postId: string): Promise<void> {
    const response = await apiClient.delete(`/campus-posts/${postId}`);
    return handleApiSuccess(response);
  }

  /**
   * Pin/unpin a campus post (Faculty only)
   * Note: Pin functionality not implemented in backend yet
   */
  static async togglePin(postId: string): Promise<{ isPinned: boolean }> {
    // TODO: Implement pin endpoint in backend
    // For now, return a mock response
    console.warn(`Pin functionality not implemented in backend yet for post ${postId}`);
    return Promise.resolve({ isPinned: false });
  }
}

export default CampusPostService;